import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import cc1ProMainImg from "@/assets/robots/cc1-pro-main.jpg";
import cc1ProDetailsImg from "@/assets/robots/cc1-pro-details.jpg";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
const CC1Pro = () => {
  const specifications = [
    { label: "Operating Voltage", value: "DC 23V~29.2V" },
    { label: "Battery Type", value: "Lithium Iron Phosphate (LiFePO₄)" },
    { label: "Battery Capacity", value: "50 Ah" },
    { label: "Charging Time", value: "About 3 hours" },
    { label: "Runtime", value: "Scrubbing: 5h | Sweeping: 5h | Carpet Vacuuming: 4h | Silent Mopping: 9h" },
    { label: "Cleaning Capabilities", value: "Sweeping, Vacuuming, Dust Mopping, Scrubbing" },
    { label: "Weight", value: "75 kg (165.35 lbs)" },
    { label: "Dimensions", value: "629 x 552 x 695 mm (24.76 x 21.73 x 27.36 in)" },
    { label: "Display", value: "10.1-inch LCD Screen" },
    { label: "Navigation", value: "LiDAR + Visual Fusion Positioning" },
    { label: "Cruising Speed", value: "0.2 m/s ~ 1.2 m/s (Adjustable)" },
    { label: "Noise Level", value: "<70 dB" },
    { label: "Min. Path Clearance", value: "70 cm (27.56 in)" },
    { label: "Cleaning Width", value: "500 mm (19.69 in) with side brush" },
    { label: "Cleaning Efficiency", value: "700-1000 m²/h (All-covered) | 1500-3000 m²/h (Spot Cleaning)" },
    { label: "Dustbin Capacity", value: "2.5 L / 6 L (Dust bag)" },
    { label: "Water Tank Capacity", value: "Clean: 15 L | Dirty: 15 L" },
    { label: "Communication", value: "4G, Wi-Fi, Bluetooth, E-gate & Elevator Control (Optional)" },
    { label: "IP Rating", value: "IPX4" }
  ];

  const keyFeatures = [
    {
      title: "PUDU VSLAM+",
      description: "Featuring fusion of VSLAM and LiDAR SLAM technologies for accurate mapping and stable navigation. No markers required. Excels in low-feature and complex environments like subway stations, airports, and warehouses."
    },
    {
      title: "AI Spot Scrubbing",
      description: "Vision-based AI recognition detects common wet stains during patrol, instantly creating optimal cleaning routes. Boosts scrubbing efficiency by up to 3× with cleaning rate of 1500-3000 m²/h."
    },
    {
      title: "AI Cleaning Intensity Control",
      description: "Automatically boosts cleaning power for stubborn stains. Monitors floor cleanliness and adjusts cleaning mode to conserve battery. Detects floor types and switches between sweep-suction and suction-only modes."
    },
    {
      title: "AI Real-Time Cleaning Performance Detection",
      description: "World's first rear AI camera monitors and evaluates cleaning performance in real-time. Detects leftover stains, triggers spot re-cleaning, and generates heatmaps for clear insight into results."
    },
    {
      title: "AI Components Self-Monitoring",
      description: "Rear AI vision system ensures effective floor cleaning and monitors components in real time. Detects issues like dirt buildup on brushes or squeegees and responds with notifications or self-cleaning cycles."
    },
    {
      title: "AI-driven Perception",
      description: "Multi-sensor fusion system accurately detects both static and dynamic obstacles. Features dynamic obstacle avoidance for wide cleaning coverage in complex environments."
    },
    {
      title: "4-in-1 Versatile Cleaning",
      description: "Combines sweeping, scrubbing, vacuuming, and dust-mopping into one multifunctional system. Includes standard sweeping, carpet vacuuming, scrubbing, silent mopping, and AI spot cleaning modes."
    },
    {
      title: "IEC 63327 Certification",
      description: "Compliant with international standards. Enhanced durability with self-cleaning brushes, contour-following brush compartments, and large metal filters."
    }
  ];

  return (
    <div className="min-h-screen pt-12 pb-8 px-4 md:pt-16" style={{
      background: `radial-gradient(900px 600px at 10% 10%, #132552 0%, #0B1736 45%, #060f24 100%)`
    }}>
      <main className="max-w-7xl mx-auto">
        <Button asChild variant="ghost" className="mb-6">
          <Link to="/robots">
            <ArrowLeft className="mr-2 w-4 h-4" /> Back to Robots
          </Link>
        </Button>

        {/* Hero Section */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <div>
            <p className="text-primary font-semibold mb-2">AI-Powered Autonomous Cleaning</p>
            <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-6xl mb-6 break-words">
              PUDU CC1 Pro
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mb-6 leading-relaxed">
              The PUDU CC1 Pro cleaning robot integrates advanced AI perception and control systems to deliver a fully automated, closed-loop cleaning process. It autonomously manages the entire cleaning cycle—from dirty debris detection and adaptive cleaning intensity adjustment to real-time cleanliness assessment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" variant="secondary" className="glow-gold">
                <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                  Book a Demo
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                  Apply for Financing
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/accessories">
                  View Accessories
                </Link>
              </Button>
            </div>
          </div>
          <div className="rounded-lg overflow-hidden bg-white/5">
            <img src={cc1ProMainImg} alt="PUDU CC1 Pro" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Key Features */}
        <section className="mb-12">
          <h2 className="font-orbitron font-bold text-3xl mb-8">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {keyFeatures.map((feature, idx) => (
              <Card key={idx} className="glass-effect border-[hsl(var(--line))] p-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Product Details Image */}
        <section className="mb-12">
          <Card className="glass-effect border-[hsl(var(--line))] p-6">
            <h2 className="font-orbitron font-bold text-2xl mb-6">Product Components</h2>
            <img src={cc1ProDetailsImg} alt="CC1 Pro Details" className="w-full rounded-lg" />
          </Card>
        </section>

        {/* Technical Specifications */}
        <section className="mb-12">
          <h2 className="font-orbitron font-bold text-3xl mb-8">Technical Specifications</h2>
          <Card className="glass-effect border-[hsl(var(--line))] p-6">
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
              {specifications.map((spec, idx) => (
                <div key={idx} className="border-b border-[hsl(var(--line))] pb-3">
                  <p className="text-sm text-muted-foreground mb-1">{spec.label}</p>
                  <p className="font-semibold">{spec.value}</p>
                </div>
              ))}
            </div>
          </Card>
        </section>

        {/* Value Proposition */}
        <section className="mb-12">
          <Card className="glass-effect border-[hsl(var(--line))] p-8 text-center">
            <h2 className="font-orbitron font-bold text-2xl mb-6">
              Designed for Complex, High-Intensity Environments
            </h2>
            <p className="text-muted-foreground mb-6 max-w-3xl mx-auto">
              The CC1 Pro combines environmental awareness with real-time decision-making to clean areas of 5,000–8,000 m² in a single run. Perfect for commercial complexes, transport hubs, hospitals, and industrial sites.
            </p>
            <div className="grid md:grid-cols-4 gap-6 mb-6 max-w-4xl mx-auto">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary mb-2">1.5-2</p>
                <p className="text-sm text-muted-foreground">Staff Replaced</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary mb-2">$46,800</p>
                <p className="text-sm text-muted-foreground">Annual Savings</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary mb-2">5 mo</p>
                <p className="text-sm text-muted-foreground">Payback Period</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary mb-2">240%</p>
                <p className="text-sm text-muted-foreground">Year 1 ROI</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              *ROI based on average labor costs. Every month after payback is pure profit.
            </p>
            <Button asChild size="lg">
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                Calculate Your Savings
              </a>
            </Button>
          </Card>
        </section>
      </main>
      
    </div>
  );
};

export default CC1Pro;
